import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  AdminUserView, AuditEntryView, InvitationView, LoginHistoryView, OrphanFileView,
  RoleMatrixView, SessionView, SystemHealthView, EmployeeAccountView, EmployeeInviteResult,
} from '../types'

const BASE = '/v1/administration'

export function useAdminUsers() {
  return useQuery({
    queryKey: ['administration', 'users'],
    queryFn: async () => (await apiClient.get<AdminUserView[]>(`${BASE}/users`)).data,
  })
}

export function useRoleMatrix() {
  return useQuery({
    queryKey: ['administration', 'role-matrix'],
    queryFn: async () => (await apiClient.get<RoleMatrixView>(`${BASE}/roles/matrix`)).data,
  })
}

export function useSessions() {
  return useQuery({
    queryKey: ['administration', 'sessions'],
    queryFn: async () => (await apiClient.get<SessionView[]>(`${BASE}/sessions`)).data,
  })
}

export function useLoginHistory(limit = 200) {
  return useQuery({
    queryKey: ['administration', 'login-history', limit],
    queryFn: async () =>
      (await apiClient.get<LoginHistoryView[]>(`${BASE}/login-history`, { params: { limit } })).data,
  })
}

export function useAuditedTables() {
  return useQuery({
    queryKey: ['administration', 'audit-tables'],
    queryFn: async () => (await apiClient.get<string[]>(`${BASE}/audit/tables`)).data,
  })
}

export function useActivity(params: { actorId?: string; type?: string; limit?: number }) {
  return useQuery({
    queryKey: ['administration', 'activity', params],
    queryFn: async () =>
      (await apiClient.get<AuditEntryView[]>(`${BASE}/audit/activity`, {
        params: {
          actorId: params.actorId || undefined,
          type: params.type || undefined,
          limit: params.limit ?? 100,
        },
      })).data,
  })
}

/** Only asked once a table and a row are both chosen — there is no "all revisions" view. */
export function useRevisions(table: string, entityId: string) {
  return useQuery({
    enabled: table.length > 0 && entityId.length > 0,
    queryKey: ['administration', 'revisions', table, entityId],
    queryFn: async () =>
      (await apiClient.get<AuditEntryView[]>(`${BASE}/audit/revisions`, {
        params: { table, entityId },
      })).data,
  })
}

export function useSystemHealth() {
  return useQuery({
    queryKey: ['administration', 'health'],
    queryFn: async () => (await apiClient.get<SystemHealthView>(`${BASE}/health`)).data,
  })
}

export function useOrphanFiles(limit = 100) {
  return useQuery({
    queryKey: ['administration', 'orphan-files', limit],
    queryFn: async () =>
      (await apiClient.get<OrphanFileView[]>(`${BASE}/orphan-files`, { params: { limit } })).data,
  })
}

export function useSysAdminMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['administration'] })

  return {
    invite: useMutation({
      mutationFn: async (body: {
        email: string; fullName: string; employeeId?: string | null; roleId?: string | null
        legalEntityId?: string | null
      }) => (await apiClient.post<InvitationView>(`${BASE}/users/invite`, body)).data,
      onSuccess: invalidate,
    }),
    reinvite: useMutation({
      mutationFn: async (userId: string) =>
        (await apiClient.post<InvitationView>(`${BASE}/users/${userId}/reinvite`, {})).data,
      onSuccess: invalidate,
    }),
    setStatus: useMutation({
      mutationFn: async (body: { userId: string; status: string; reason?: string | null }) =>
        (await apiClient.put<AdminUserView[]>(`${BASE}/users/${body.userId}/status`, {
          status: body.status, reason: body.reason ?? null,
        })).data,
      onSuccess: invalidate,
    }),
    setMfa: useMutation({
      mutationFn: async (body: { userId: string; enabled: boolean }) =>
        (await apiClient.put<AdminUserView[]>(`${BASE}/users/${body.userId}/mfa`, {
          enabled: body.enabled,
        })).data,
      onSuccess: invalidate,
    }),
    forceLogout: useMutation({
      mutationFn: async (body: { userId: string; reason?: string }) =>
        (await apiClient.post<SessionView[]>(`${BASE}/users/${body.userId}/logout`, null, {
          params: { reason: body.reason || undefined },
        })).data,
      onSuccess: invalidate,
    }),
    createRole: useMutation({
      mutationFn: async (body: { code: string; name: string; description?: string | null }) =>
        (await apiClient.post<RoleMatrixView>(`${BASE}/roles`, body)).data,
      onSuccess: invalidate,
    }),
    setRolePermissions: useMutation({
      mutationFn: async (body: { roleId: string; permissions: string[] }) =>
        (await apiClient.put<RoleMatrixView>(`${BASE}/roles/${body.roleId}/permissions`, {
          permissions: body.permissions,
        })).data,
      onSuccess: invalidate,
    }),
  }
}

/**
 * Inviting employees from their records — one from the profile, or several from the directory.
 * Each comes back with its own outcome; somebody without an email is reported, not dropped.
 */
export function useInviteEmployees() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { employeeIds: string[]; roleId?: string | null }) =>
      (await apiClient.post<EmployeeInviteResult[]>(`${BASE}/users/invite-employees`, body)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['administration'] })
    },
  })
}

/** Whether an employee can already sign in — the profile hides "Invite" when they can. */
export function useEmployeeAccount(employeeId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['administration', 'employee-account', employeeId],
    enabled: enabled && Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<EmployeeAccountView>(`${BASE}/users/for-employee/${employeeId}`)).data,
  })
}
